/**
 * Digital Office — avatar model + character renderer.
 *
 * Characters are drawn procedurally on a 2D canvas (no sprite sheets), in a
 * soft, rounded "modern Sims" style: a 3/4 top-down little person with a
 * round head, a hairstyle, a rounded torso, legs that scissor while
 * walking, and a soft contact shadow. Every option below is a plain string
 * key so the persisted row (`digital_office_avatars`) stays readable and
 * adding an option never needs a migration.
 */

export type Facing = 'up' | 'down' | 'left' | 'right';

export interface AvatarConfig {
  skin_tone: string;
  hair_style: string;
  hair_color: string;
  top_color: string;
  bottom_color: string;
  accessory: string;
}

export interface AvatarOption {
  id: string;
  label: string;
  /** Swatch colour for palette options. */
  hex?: string;
}

export const SKIN_TONES: AvatarOption[] = [
  { id: 'porcelain', label: 'Porcelain', hex: '#F5DCC6' },
  { id: 'fair', label: 'Fair', hex: '#EEC3A2' },
  { id: 'tan', label: 'Tan', hex: '#D9A277' },
  { id: 'olive', label: 'Olive', hex: '#C08A5C' },
  { id: 'brown', label: 'Brown', hex: '#8D5A3B' },
  { id: 'deep', label: 'Deep', hex: '#5B3A26' },
];

export const HAIR_STYLES: AvatarOption[] = [
  { id: 'short', label: 'Short' },
  { id: 'buzz', label: 'Buzz' },
  { id: 'bob', label: 'Bob' },
  { id: 'long', label: 'Long' },
  { id: 'curly', label: 'Curly' },
  { id: 'bun', label: 'Bun' },
  { id: 'none', label: 'None' },
];

export const HAIR_COLORS: AvatarOption[] = [
  { id: 'black', label: 'Black', hex: '#2A2320' },
  { id: 'brown', label: 'Brown', hex: '#5C3C25' },
  { id: 'chestnut', label: 'Chestnut', hex: '#8B5A2B' },
  { id: 'blonde', label: 'Blonde', hex: '#D9B16A' },
  { id: 'red', label: 'Auburn', hex: '#A5472A' },
  { id: 'grey', label: 'Silver', hex: '#A9A9AE' },
];

/** Outfit palette — leans on the Virro brand colours so the room feels on-brand. */
export const TOP_COLORS: AvatarOption[] = [
  { id: 'moss', label: 'Bright Moss', hex: '#C5DD85' },
  { id: 'humus', label: 'Humus', hex: '#1B2E1D' },
  { id: 'old-growth', label: 'Old Growth', hex: '#4B5E43' },
  { id: 'atmosphere', label: 'Atmosphere', hex: '#AAD8FA' },
  { id: 'salmon', label: 'Fresh Salmon', hex: '#E8907A' },
  { id: 'linen', label: 'Linen', hex: '#EFEADF' },
  { id: 'ocean', label: 'Ocean', hex: '#0E415E' },
  { id: 'plum', label: 'Plum', hex: '#6B4A7A' },
];

export const BOTTOM_COLORS: AvatarOption[] = [
  { id: 'slate', label: 'Slate', hex: '#3F4752' },
  { id: 'denim', label: 'Denim', hex: '#3B5A86' },
  { id: 'khaki', label: 'Khaki', hex: '#B8A77F' },
  { id: 'black', label: 'Black', hex: '#1E1E22' },
  { id: 'olive', label: 'Olive', hex: '#5F6B3A' },
  { id: 'stone', label: 'Stone', hex: '#8E8B84' },
];

export const ACCESSORIES: AvatarOption[] = [
  { id: 'none', label: 'None' },
  { id: 'glasses', label: 'Glasses' },
  { id: 'headphones', label: 'Headphones' },
  { id: 'cap', label: 'Cap' },
  { id: 'beanie', label: 'Beanie' },
];

export const DEFAULT_AVATAR: AvatarConfig = {
  skin_tone: 'tan',
  hair_style: 'short',
  hair_color: 'brown',
  top_color: 'moss',
  bottom_color: 'slate',
  accessory: 'none',
};

function hexOf(list: AvatarOption[], id: string, fallback: string): string {
  return list.find((o) => o.id === id)?.hex ?? fallback;
}

function pick<T>(list: T[], seed: number): T {
  return list[Math.abs(seed) % list.length];
}

/** Deterministic "random" avatar from a string (e.g. user id) — used as the starting point in the creator. */
export function seededAvatar(seed: string): AvatarConfig {
  let h = 0;
  for (let i = 0; i < seed.length; i++) h = (h * 31 + seed.charCodeAt(i)) | 0;
  return {
    skin_tone: pick(SKIN_TONES, h).id,
    hair_style: pick(HAIR_STYLES.filter((s) => s.id !== 'none'), h >> 3).id,
    hair_color: pick(HAIR_COLORS, h >> 6).id,
    top_color: pick(TOP_COLORS, h >> 9).id,
    bottom_color: pick(BOTTOM_COLORS, h >> 12).id,
    accessory: pick(ACCESSORIES, h >> 15).id,
  };
}

/** Normalise an arbitrary row into a valid config (unknown keys fall back to defaults). */
export function normalizeAvatar(raw: Partial<AvatarConfig> | null | undefined): AvatarConfig {
  const has = (list: AvatarOption[], id: unknown): id is string =>
    typeof id === 'string' && list.some((o) => o.id === id);
  return {
    skin_tone: has(SKIN_TONES, raw?.skin_tone) ? raw!.skin_tone! : DEFAULT_AVATAR.skin_tone,
    hair_style: has(HAIR_STYLES, raw?.hair_style) ? raw!.hair_style! : DEFAULT_AVATAR.hair_style,
    hair_color: has(HAIR_COLORS, raw?.hair_color) ? raw!.hair_color! : DEFAULT_AVATAR.hair_color,
    top_color: has(TOP_COLORS, raw?.top_color) ? raw!.top_color! : DEFAULT_AVATAR.top_color,
    bottom_color: has(BOTTOM_COLORS, raw?.bottom_color) ? raw!.bottom_color! : DEFAULT_AVATAR.bottom_color,
    accessory: has(ACCESSORIES, raw?.accessory) ? raw!.accessory! : DEFAULT_AVATAR.accessory,
  };
}

// ───────────────────────────────────────────────────────────────────────────
// Colour helpers
// ───────────────────────────────────────────────────────────────────────────

export function shade(hex: string, amount: number): string {
  const n = parseInt(hex.slice(1), 16);
  const r = Math.max(0, Math.min(255, ((n >> 16) & 0xff) + amount));
  const g = Math.max(0, Math.min(255, ((n >> 8) & 0xff) + amount));
  const b = Math.max(0, Math.min(255, (n & 0xff) + amount));
  return `rgb(${r},${g},${b})`;
}

function isLight(hex: string): boolean {
  const n = parseInt(hex.slice(1), 16);
  const r = (n >> 16) & 0xff;
  const g = (n >> 8) & 0xff;
  const b = n & 0xff;
  return (r * 299 + g * 587 + b * 114) / 1000 > 170;
}

// ───────────────────────────────────────────────────────────────────────────
// Renderer
// ───────────────────────────────────────────────────────────────────────────

export interface DrawCharacterOptions {
  /** World/canvas x of the character's FEET (the contact point). */
  x: number;
  y: number;
  /** Scale factor (1 ≈ 30 units tall). */
  scale?: number;
  facing?: Facing;
  /** 0..1 walking cycle phase; undefined = standing still. */
  walkPhase?: number;
  /** 0..1 — faded ghost for offline teammates. */
  alpha?: number;
}

function roundRect(ctx: CanvasRenderingContext2D, x: number, y: number, w: number, h: number, r: number) {
  const rr = Math.min(r, w / 2, h / 2);
  ctx.beginPath();
  ctx.moveTo(x + rr, y);
  ctx.lineTo(x + w - rr, y);
  ctx.quadraticCurveTo(x + w, y, x + w, y + rr);
  ctx.lineTo(x + w, y + h - rr);
  ctx.quadraticCurveTo(x + w, y + h, x + w - rr, y + h);
  ctx.lineTo(x + rr, y + h);
  ctx.quadraticCurveTo(x, y + h, x, y + h - rr);
  ctx.lineTo(x, y + rr);
  ctx.quadraticCurveTo(x, y, x + rr, y);
  ctx.closePath();
}

/**
 * Draw one character. The coordinate system is whatever the caller has
 * already set up (the game canvas applies camera transforms first), so
 * this is also used verbatim by the avatar-creator preview.
 */
export function drawCharacter(
  ctx: CanvasRenderingContext2D,
  cfg: AvatarConfig,
  opts: DrawCharacterOptions,
): void {
  const { x, y } = opts;
  const s = opts.scale ?? 1;
  const facing = opts.facing ?? 'down';
  const alpha = opts.alpha ?? 1;
  const walking = opts.walkPhase !== undefined;
  const phase = opts.walkPhase ?? 0;
  const bob = walking ? Math.sin(phase * Math.PI * 2) * 0.8 * s : 0;

  const skin = hexOf(SKIN_TONES, cfg.skin_tone, '#D9A277');
  const hair = hexOf(HAIR_COLORS, cfg.hair_color, '#5C3C25');
  const top = hexOf(TOP_COLORS, cfg.top_color, '#C5DD85');
  const bottom = hexOf(BOTTOM_COLORS, cfg.bottom_color, '#3F4752');

  ctx.save();
  ctx.globalAlpha = alpha;

  // Contact shadow
  ctx.fillStyle = 'rgba(20, 30, 20, 0.22)';
  ctx.beginPath();
  ctx.ellipse(x, y, 7.5 * s, 3 * s, 0, 0, Math.PI * 2);
  ctx.fill();

  // Legs — scissor while walking
  const legSwing = walking ? Math.sin(phase * Math.PI * 2) * 2.2 * s : 0;
  const legW = 3.6 * s;
  const legH = 6 * s;
  const legY = y - legH - 0.5 * s + bob;
  const sideways = facing === 'left' || facing === 'right';
  ctx.fillStyle = bottom;
  if (sideways) {
    roundRect(ctx, x - legW / 2 - legSwing * 0.8, legY, legW, legH, 1.6 * s);
    ctx.fill();
    roundRect(ctx, x - legW / 2 + legSwing * 0.8, legY, legW, legH, 1.6 * s);
    ctx.fill();
  } else {
    roundRect(ctx, x - legW - 0.6 * s, legY - Math.max(0, legSwing) * 0.3, legW, legH, 1.6 * s);
    ctx.fill();
    roundRect(ctx, x + 0.6 * s, legY - Math.max(0, -legSwing) * 0.3, legW, legH, 1.6 * s);
    ctx.fill();
  }

  // Torso
  const torsoW = 13 * s;
  const torsoH = 11 * s;
  const torsoY = y - legH - torsoH + 1.5 * s + bob;
  ctx.fillStyle = top;
  roundRect(ctx, x - torsoW / 2, torsoY, torsoW, torsoH, 4.5 * s);
  ctx.fill();
  // Torso shading (right side a touch darker for volume)
  ctx.fillStyle = 'rgba(0,0,0,0.08)';
  roundRect(ctx, x + torsoW / 2 - 4 * s, torsoY, 4 * s, torsoH, 3 * s);
  ctx.fill();

  // Arms
  const armW = 3.2 * s;
  const armH = 8 * s;
  const armSwing = walking ? Math.sin(phase * Math.PI * 2 + Math.PI) * 1.6 * s : 0;
  ctx.fillStyle = shade(top, isLight(top) ? -18 : 14);
  if (facing === 'left') {
    roundRect(ctx, x - 2 * s + armSwing * 0.4, torsoY + 2 * s, armW, armH, 1.6 * s);
    ctx.fill();
  } else if (facing === 'right') {
    roundRect(ctx, x - 1.2 * s - armSwing * 0.4, torsoY + 2 * s, armW, armH, 1.6 * s);
    ctx.fill();
  } else {
    roundRect(ctx, x - torsoW / 2 - 1.2 * s, torsoY + 1.5 * s + armSwing, armW, armH, 1.6 * s);
    ctx.fill();
    roundRect(ctx, x + torsoW / 2 - 2 * s, torsoY + 1.5 * s - armSwing, armW, armH, 1.6 * s);
    ctx.fill();
  }
  // Hands
  ctx.fillStyle = skin;
  if (facing === 'left') {
    ctx.beginPath(); ctx.arc(x - 0.4 * s + armSwing * 0.4, torsoY + 2 * s + armH, 1.7 * s, 0, Math.PI * 2); ctx.fill();
  } else if (facing === 'right') {
    ctx.beginPath(); ctx.arc(x + 0.4 * s - armSwing * 0.4, torsoY + 2 * s + armH, 1.7 * s, 0, Math.PI * 2); ctx.fill();
  } else {
    ctx.beginPath(); ctx.arc(x - torsoW / 2 + 0.4 * s, torsoY + 1.5 * s + armH + armSwing, 1.7 * s, 0, Math.PI * 2); ctx.fill();
    ctx.beginPath(); ctx.arc(x + torsoW / 2 - 0.4 * s, torsoY + 1.5 * s + armH - armSwing, 1.7 * s, 0, Math.PI * 2); ctx.fill();
  }

  // Head
  const headR = 7 * s;
  const headCx = x;
  const headCy = torsoY - headR + 2.2 * s;
  ctx.fillStyle = skin;
  ctx.beginPath();
  ctx.arc(headCx, headCy, headR, 0, Math.PI * 2);
  ctx.fill();
  // Neck shadow
  ctx.fillStyle = 'rgba(0,0,0,0.10)';
  ctx.beginPath();
  ctx.ellipse(headCx, headCy + headR - 0.6 * s, headR * 0.6, 1.4 * s, 0, 0, Math.PI);
  ctx.fill();

  // Face (not when walking away)
  if (facing !== 'up') {
    const eyeY = headCy + 0.6 * s;
    const eyeDx = facing === 'down' ? 2.6 * s : facing === 'left' ? -2.4 * s : 2.4 * s;
    ctx.fillStyle = '#2A2320';
    if (facing === 'down') {
      ctx.beginPath(); ctx.arc(headCx - eyeDx, eyeY, 0.9 * s, 0, Math.PI * 2); ctx.fill();
      ctx.beginPath(); ctx.arc(headCx + eyeDx, eyeY, 0.9 * s, 0, Math.PI * 2); ctx.fill();
      // Smile
      ctx.strokeStyle = 'rgba(60,30,20,0.55)';
      ctx.lineWidth = 0.8 * s;
      ctx.beginPath();
      ctx.arc(headCx, eyeY + 1.6 * s, 2 * s, Math.PI * 0.15, Math.PI * 0.85);
      ctx.stroke();
    } else {
      ctx.beginPath(); ctx.arc(headCx + eyeDx, eyeY, 0.9 * s, 0, Math.PI * 2); ctx.fill();
      ctx.beginPath(); ctx.arc(headCx + eyeDx * 0.35, eyeY, 0.9 * s, 0, Math.PI * 2); ctx.fill();
    }
    // Blush
    ctx.fillStyle = 'rgba(230,120,110,0.22)';
    ctx.beginPath(); ctx.ellipse(headCx - 3.4 * s, eyeY + 2 * s, 1.5 * s, 0.9 * s, 0, 0, Math.PI * 2); ctx.fill();
    ctx.beginPath(); ctx.ellipse(headCx + 3.4 * s, eyeY + 2 * s, 1.5 * s, 0.9 * s, 0, 0, Math.PI * 2); ctx.fill();
  }

  // Hair
  drawHair(ctx, cfg.hair_style, hair, headCx, headCy, headR, facing, s);

  // Accessory
  drawAccessory(ctx, cfg.accessory, headCx, headCy, headR, facing, s, hair);

  ctx.restore();
}

function drawHair(
  ctx: CanvasRenderingContext2D,
  style: string,
  color: string,
  cx: number,
  cy: number,
  r: number,
  facing: Facing,
  s: number,
) {
  if (style === 'none') return;
  ctx.fillStyle = color;
  const back = facing === 'up';
  switch (style) {
    case 'buzz': {
      ctx.beginPath();
      ctx.arc(cx, cy, r * 1.02, Math.PI * 1.05, Math.PI * 1.95);
      ctx.lineTo(cx + r * 0.95, cy - r * 0.15);
      ctx.lineTo(cx - r * 0.95, cy - r * 0.15);
      ctx.closePath();
      ctx.fill();
      break;
    }
    case 'short': {
      ctx.beginPath();
      ctx.arc(cx, cy - 0.4 * s, r * 1.06, Math.PI * 1.0, Math.PI * 2.0);
      ctx.lineTo(cx + r * 1.06, cy + (back ? r * 0.5 : r * 0.05));
      ctx.lineTo(cx - r * 1.06, cy + (back ? r * 0.5 : r * 0.05));
      ctx.closePath();
      ctx.fill();
      // Fringe tuft
      if (!back) {
        ctx.beginPath();
        ctx.ellipse(cx - r * 0.35, cy - r * 0.55, r * 0.55, r * 0.35, -0.3, 0, Math.PI * 2);
        ctx.fill();
      }
      break;
    }
    case 'bob': {
      ctx.beginPath();
      ctx.arc(cx, cy - 0.3 * s, r * 1.1, Math.PI, Math.PI * 2);
      ctx.lineTo(cx + r * 1.1, cy + r * 0.75);
      ctx.quadraticCurveTo(cx + r * 1.1, cy + r * 1.05, cx + r * 0.75, cy + r * 1.0);
      if (back) {
        ctx.lineTo(cx - r * 0.75, cy + r * 1.0);
      } else {
        ctx.lineTo(cx + r * 0.8, cy + r * 0.2);
        ctx.lineTo(cx - r * 0.8, cy + r * 0.2);
        ctx.lineTo(cx - r * 0.75, cy + r * 1.0);
      }
      ctx.quadraticCurveTo(cx - r * 1.1, cy + r * 1.05, cx - r * 1.1, cy + r * 0.75);
      ctx.closePath();
      ctx.fill();
      break;
    }
    case 'long': {
      ctx.beginPath();
      ctx.arc(cx, cy - 0.3 * s, r * 1.1, Math.PI, Math.PI * 2);
      ctx.lineTo(cx + r * 1.1, cy + r * 1.6);
      ctx.quadraticCurveTo(cx + r * 1.0, cy + r * 1.9, cx + r * 0.7, cy + r * 1.8);
      if (back) {
        ctx.lineTo(cx - r * 0.7, cy + r * 1.8);
      } else {
        ctx.lineTo(cx + r * 0.75, cy + r * 0.1);
        ctx.lineTo(cx - r * 0.75, cy + r * 0.1);
        ctx.lineTo(cx - r * 0.7, cy + r * 1.8);
      }
      ctx.quadraticCurveTo(cx - r * 1.0, cy + r * 1.9, cx - r * 1.1, cy + r * 1.6);
      ctx.closePath();
      ctx.fill();
      break;
    }
    case 'curly': {
      const blobs = 7;
      for (let i = 0; i < blobs; i++) {
        const a = Math.PI + (i / (blobs - 1)) * Math.PI;
        const bx = cx + Math.cos(a) * r * 0.95;
        const by = cy - 0.3 * s + Math.sin(a) * r * 0.95;
        ctx.beginPath();
        ctx.arc(bx, by, r * 0.42, 0, Math.PI * 2);
        ctx.fill();
      }
      ctx.beginPath();
      ctx.arc(cx, cy - 0.3 * s, r * 1.0, Math.PI, Math.PI * 2);
      ctx.lineTo(cx + r, cy + (back ? r * 0.6 : r * 0.0));
      ctx.lineTo(cx - r, cy + (back ? r * 0.6 : r * 0.0));
      ctx.closePath();
      ctx.fill();
      break;
    }
    case 'bun': {
      ctx.beginPath();
      ctx.arc(cx, cy - 0.3 * s, r * 1.05, Math.PI, Math.PI * 2);
      ctx.lineTo(cx + r * 1.05, cy + (back ? r * 0.5 : r * 0.0));
      ctx.lineTo(cx - r * 1.05, cy + (back ? r * 0.5 : r * 0.0));
      ctx.closePath();
      ctx.fill();
      ctx.beginPath();
      ctx.arc(cx, cy - r * 1.15, r * 0.42, 0, Math.PI * 2);
      ctx.fill();
      break;
    }
    default:
      break;
  }
}

function drawAccessory(
  ctx: CanvasRenderingContext2D,
  accessory: string,
  cx: number,
  cy: number,
  r: number,
  facing: Facing,
  s: number,
  hairHex: string,
) {
  switch (accessory) {
    case 'glasses': {
      if (facing === 'up') return;
      ctx.strokeStyle = 'rgba(30,30,40,0.85)';
      ctx.lineWidth = 0.7 * s;
      const eyeY = cy + 0.6 * s;
      if (facing === 'down') {
        ctx.beginPath(); ctx.arc(cx - 2.6 * s, eyeY, 1.8 * s, 0, Math.PI * 2); ctx.stroke();
        ctx.beginPath(); ctx.arc(cx + 2.6 * s, eyeY, 1.8 * s, 0, Math.PI * 2); ctx.stroke();
        ctx.beginPath(); ctx.moveTo(cx - 0.8 * s, eyeY); ctx.lineTo(cx + 0.8 * s, eyeY); ctx.stroke();
      } else {
        const dir = facing === 'left' ? -1 : 1;
        ctx.beginPath(); ctx.arc(cx + dir * 2.4 * s, eyeY, 1.8 * s, 0, Math.PI * 2); ctx.stroke();
        ctx.beginPath(); ctx.moveTo(cx + dir * 0.6 * s, eyeY); ctx.lineTo(cx - dir * 4 * s, eyeY - 0.6 * s); ctx.stroke();
      }
      return;
    }
    case 'headphones': {
      ctx.strokeStyle = '#2A2A30';
      ctx.lineWidth = 1.2 * s;
      ctx.beginPath();
      ctx.arc(cx, cy - 0.2 * s, r * 1.08, Math.PI * 1.05, Math.PI * 1.95);
      ctx.stroke();
      ctx.fillStyle = '#2A2A30';
      roundRect(ctx, cx - r * 1.08 - 1.4 * s, cy - 1.6 * s, 2.8 * s, 4.2 * s, 1.2 * s);
      ctx.fill();
      roundRect(ctx, cx + r * 1.08 - 1.4 * s, cy - 1.6 * s, 2.8 * s, 4.2 * s, 1.2 * s);
      ctx.fill();
      ctx.fillStyle = '#C5DD85';
      roundRect(ctx, cx - r * 1.08 - 0.7 * s, cy - 0.6 * s, 1.4 * s, 2.2 * s, 0.6 * s);
      ctx.fill();
      roundRect(ctx, cx + r * 1.08 - 0.7 * s, cy - 0.6 * s, 1.4 * s, 2.2 * s, 0.6 * s);
      ctx.fill();
      return;
    }
    case 'cap': {
      ctx.fillStyle = '#1B2E1D';
      ctx.beginPath();
      ctx.arc(cx, cy - 0.6 * s, r * 1.08, Math.PI, Math.PI * 2);
      ctx.closePath();
      ctx.fill();
      // Brim
      ctx.fillStyle = '#16261A';
      if (facing === 'down') {
        roundRect(ctx, cx - r * 1.2, cy - 1.4 * s, r * 2.4, 2 * s, 1 * s);
      } else if (facing === 'left') {
        roundRect(ctx, cx - r * 1.9, cy - 1.4 * s, r * 1.6, 2 * s, 1 * s);
      } else if (facing === 'right') {
        roundRect(ctx, cx + r * 0.3, cy - 1.4 * s, r * 1.6, 2 * s, 1 * s);
      } else {
        roundRect(ctx, cx - r * 0.9, cy - 1.0 * s, r * 1.8, 1.6 * s, 0.8 * s);
      }
      ctx.fill();
      ctx.fillStyle = '#C5DD85';
      ctx.beginPath();
      ctx.arc(cx, cy - r * 0.45, 1.2 * s, 0, Math.PI * 2);
      ctx.fill();
      return;
    }
    case 'beanie': {
      ctx.fillStyle = '#E8907A';
      ctx.beginPath();
      ctx.arc(cx, cy - 0.4 * s, r * 1.1, Math.PI, Math.PI * 2);
      ctx.closePath();
      ctx.fill();
      roundRect(ctx, cx - r * 1.1, cy - 1.6 * s, r * 2.2, 2.4 * s, 1 * s);
      ctx.fill();
      ctx.fillStyle = shade(hairHex, 0);
      ctx.beginPath();
      ctx.arc(cx, cy - r * 1.05, 1.6 * s, 0, Math.PI * 2);
      ctx.fillStyle = '#F5EDE6';
      ctx.fill();
      return;
    }
    default:
      return;
  }
}

/** Approximate height of a character above its feet, for label placement. */
export function characterHeight(scale = 1): number {
  return 30 * scale;
}
